import anime from 'animejs/lib/anime.min.js';
import SmoothScroll from 'smooth-scroll/dist/smooth-scroll.min.js';
import DOMHelper from "../../lib/dom-helper";
import { EasingFunctions } from "../../lib/animation-module";
import { throttle } from "../../lib";

export class TocService {
	constructor() {
		this.smoothScroll = new SmoothScroll();
	}

	get scrollPosTopMargin() {
		const media = window.matchMedia('(max-width: 600px)').matches;
		return media ? 78 : 72;
	}

	scrollToSection(id, animate) {
		animate = animate == null ? true : animate;
		const $el = document.querySelector(`[data-toc-id="${id}"]`);

		if (!$el) {
			return;
		}

		let scrollToPos = DOMHelper.getElementTopPosition($el);
		scrollToPos -= this.scrollPosTopMargin;

		// Prevent bug when scrolling gets stuck on repeating scroll trigger
		// if window is on section's position
		// but window scrollY pos value is float number
		if (scrollToPos === Math.floor(window.scrollY) || scrollToPos === Math.ceil(window.scrollY)) {
			return;
		}

		this.smoothScroll.animateScroll(scrollToPos, null, {
			speed: animate ? 360 : 10,
			customEasing: EasingFunctions.primary
		});
	}

	scrollListToActiveLink($container) {
		const $link = $container.querySelector('.toc-list_link.-active');

		if ($link == null) {
			return;
		}

		const y = $link.offsetTop - 10;
		$container.scrollTo(0, y);
	}
}

export class TocDesktop {
	constructor($root, options = {}) {
		this.$root = $root;
		this.$trigger = null;
		this.$list = null;
		this.$inner = null;
		this.$linkList = null;
		this.$links = null;
		this.$sections = null;

		this.shouldAnimateScroll = options.shouldAnimateScroll == null ? true : options.shouldAnimateScroll;

		this.tocService = new TocService();
		this.sectionPositions = {};
		this.lastSectionYBottomPos = null;
		this.activeSectionId = null;

		this.mediaDesktops = window.matchMedia('(min-width: 1281px)').matches;
		this.mediaLaptops = window.matchMedia('(min-width: 601px) and (max-width: 1280px)').matches;

		if (this.$root) {
			this.init();
		}
	}

	init() {
		this.$trigger = this.$root.querySelector('.toc-trigger');
		this.$list = this.$root.querySelector('.toc-list');
		this.$inner = this.$root.querySelector('.toc-list_inner');
		this.$linkList = this.$root.querySelector('.toc-list_list');
		this.$links = this.$list.querySelectorAll('a');
		this.$sections = document.querySelectorAll('[data-toc-id]');

		this.setHeight();
		this.collectSectionPositions();
		this.addEventListeners();
		this.tocService.scrollListToActiveLink(this.$inner);

		if (Object.keys(this.sectionPositions).includes(window.location.hash.slice(1))) {
			window.scrollTo(0, 0);
			this.tocService.scrollToSection(window.location.hash.slice(1), false);
		}
	}

	setHeight() {
		let $activeList = this.$linkList;
		const activeListId = this.$root.dataset.tocActiveList;

		if (activeListId) {
			$activeList = this.$root.querySelector(`[data-toc-list="${activeListId}"]`);
		}

		const maxHeight = window.innerHeight - (30 + 36 + 115);
		let height = Array.from($activeList.children).reduce((acc, cur, index) => {
			return index === 0 ? acc + cur.offsetHeight : acc + cur.offsetHeight + 10;
		}, 60);

		if (this.mediaLaptops) {
			height = Array.from($activeList.children).reduce((acc, cur) => (
				acc + cur.offsetHeight
			), this.listSwitch ? 62 : 16);
		}

		this.$list.style.height = height > maxHeight ? maxHeight + 'px' : height + 'px';
		this.$inner.scrollTo(0, 0);

		if (height > 740) {
			this.$inner.style.overflowY = 'auto';
		} else {
			this.$inner.style.overflowY = 'hidden';
		}
	}

	refreshStateOnResize() {
		this.setHeight();
		this.collectSectionPositions();
		this.hideOnResize();
	}

	collectSectionPositions() {
		// Collect all section Y positions (minus top margin)
		this.$sections.forEach(($section, i) => {
			let sectionYPos = DOMHelper.getElementTopPosition($section) - this.tocService.scrollPosTopMargin - 5;
			this.sectionPositions[$section.dataset.tocId] = sectionYPos;

			if (i === this.$sections.length - 1) {
				this.lastSectionYBottomPos = sectionYPos + $section.offsetHeight;
			}
		});
	}

	addEventListeners() {
		// Show toc on button click
		this.$trigger.addEventListener('click', () => {
			this.checkMediaBreakpoints();

			if (this.mediaLaptops) {
				if (this.$trigger.classList.contains('-active')) {
					this.hideOnLaptops();
				} else {
					this.showOnLaptops();
				}
			}
		});

		// Hide toc on click on any place except toc root
		window.addEventListener('click', (e) => {
			if (!this.mediaLaptops || e.target.closest('.toc') || !this.$trigger.classList.contains('-active')) {
				return;
			}

			this.hideOnLaptops();
		});

		// Add scroll to section event listeners
		this.$links.forEach(($link) => {
			$link.addEventListener('click', () => {
				const id = $link.href.split('#')[1];
				this.tocService.scrollToSection(id, this.shouldAnimateScroll);
			});
		});

		// Highlight link to the section when it falls into viewport
		window.addEventListener('scroll', throttle(() => {
			this.collectSectionPositions();
			const scrollYPos = window.scrollY;
			let potentialActiveSectionId = null;
			let nextSectionId = null;

			// if current scroll position overlaps any of the section position
			// remember this and next positions
			for (let sectionId in this.sectionPositions) {
				const sectionYPos = this.sectionPositions[sectionId];

				if (scrollYPos >= sectionYPos && scrollYPos < this.lastSectionYBottomPos) {
					potentialActiveSectionId = sectionId;
				} else if (scrollYPos < sectionYPos) {
					nextSectionId = sectionId;
					break;
				}
			}

			// If there is potential active section set it in active state;
			// Otherwise drop active state
			if (
				potentialActiveSectionId &&
				potentialActiveSectionId !== nextSectionId &&
				potentialActiveSectionId !== this.activeSectionId
			) {
				this.$links.forEach(($link) => {
					const linkId = $link.href.split('#')[1];

					if ($link.classList.contains('-active')) {
						$link.classList.remove('-active');
					} else if (potentialActiveSectionId === linkId) {
						$link.classList.add('-active');
						this.activeSectionId = potentialActiveSectionId;
					}
				});
			} else if (!potentialActiveSectionId && this.activeSectionId) {
				this.$links.forEach(($link) => {
					if ($link.classList.contains('-active')) {
						$link.classList.remove('-active');
						this.activeSectionId = null;
					}
				});
			}
		}, 20));
	}

	checkMediaBreakpoints() {
		this.mediaDesktops = window.matchMedia('(min-width: 1281px)').matches;
		this.mediaLaptops = window.matchMedia('(min-width: 601px) and (max-width: 1280px)').matches;
	}

	hideOnResize() {
		this.checkMediaBreakpoints();

		if (this.mediaLaptops) {
			this.$list.classList.remove('-hidden');
			this.$list.removeAttribute('style');

			if (this.$trigger.classList.contains('-active')) {
				this.hideOnLaptops();
			}
		}
	}

	showOnLaptops() {
		this.$trigger.classList.add('-active');
		this.$list.classList.add('-shown');
		this.setHeight();
		this.tocService.scrollListToActiveLink(this.$inner);

		anime({
			targets: this.$list,
			opacity: [0, 1],
			scale: [0.95, 1],
			duration: 240,
			easing: 'cubicBezier(0.32, 0.08, 0.24, 1)'
		});
	}

	hideOnLaptops() {
		this.$trigger.classList.remove('-active');

		anime({
			targets: this.$list,
			opacity: [1, 0],
			duration: 120,
			easing: 'cubicBezier(0.32, 0.08, 0.24, 1)',
			complete: (anim) => {
				if (anim.completed) {
					this.$list.classList.remove('-shown');
				}
			}
		});
	}
}

export class TocMobile {
	constructor($root, options = {}) {
		this.$root = $root;
		this.$trigger = null;
		this.$listContainer = null;
		this.$list = null;
		this.$links = null;

		this.shouldAnimateScroll = options.shouldAnimateScroll == null ? true : options.shouldAnimateScroll;

		this.tocService = new TocService();

		this.shown = false;

		if (this.$root) {
			this.init();
		}
	}

	init() {
		this.$trigger = this.$root.querySelector('.toc-trigger-show');
		this.$listContainer = this.$root.querySelector('.toc-list-mobile');
		this.$list = this.$root.querySelector('.toc-list-mobile_list');
		this.$links = this.$list.querySelectorAll('a');

		this.addEventListeners();
	}

	addEventListeners() {
		// Add show/hide toc event listener
		this.$trigger.addEventListener('click', () => {
			if (!this.$listContainer.offsetHeight) {
				this.$trigger.classList.add('-active');
				this.$listContainer.style.maxHeight = this.$list.offsetHeight + 15 + 'px';
			} else {
				this.$trigger.classList.remove('-active');
				this.$listContainer.removeAttribute('style');
			}
		});

		// Add scroll to section event listeners
		this.$links.forEach(($link) => {
			$link.addEventListener('click', () => {
				const id = $link.href.split('#')[1];
				this.tocService.scrollToSection(id, this.shouldAnimateScroll);
			});
		});
	}
}

export default class Toc {
	constructor(TocDesktopClass = null, TocMobileClass = null, options = {}) {
		this.$tocPostDesktop = document.getElementById('tocPost');
		this.$tocBookDesktop = document.getElementById('tocBook');
		this.$tocMobile = document.getElementById('TocMobile');

		this.media = window.matchMedia('(min-width: 601px)').matches;

		this.TocDesktopClass = TocDesktopClass || TocDesktop;
		this.TocMobileClass = TocMobileClass || TocMobile;
		this.tocPostDesktopModule = null;
		this.tocBookDesktopModule = null;
		this.mobileModule = null;

		this.shouldAnimateScroll = options.shouldAnimateScroll == null ? true : options.shouldAnimateScroll;

		this.init();
	}

	init() {
		if (this.media) {
			if (this.$tocPostDesktop) {
				this.tocPostDesktopModule = new this.TocDesktopClass(this.$tocPostDesktop, { shouldAnimateScroll: this.shouldAnimateScroll });
			}

			if (this.$tocBookDesktop) {
				this.tocBookDesktopModule = new this.TocDesktopClass(this.$tocBookDesktop, { shouldAnimateScroll: this.shouldAnimateScroll });
			}
		} else if (!this.media && this.$tocMobile) {
			this.mobileModule = new this.TocMobileClass(this.$tocMobile, { shouldAnimateScroll: this.shouldAnimateScroll });
		}

		if (this.$tocPostDesktop || this.$tocBookDesktop || this.$tocMobile) {
			window.addEventListener('resize', throttle(() => {
				this.media = window.matchMedia('(min-width: 601px)').matches;

				if (this.media) {
					if (this.$tocPostDesktop) {
						if (!this.tocPostDesktopModule) {
							this.tocPostDesktopModule = new this.TocDesktopClass(this.$tocPostDesktop, { shouldAnimateScroll: this.shouldAnimateScroll });
						} else {
							this.tocPostDesktopModule.refreshStateOnResize();
						}
					}

					if (this.$tocBookDesktop) {
						if (!this.tocBookDesktopModule) {
							this.tocBookDesktopModule = new this.TocDesktopClass(this.$tocBookDesktop, { shouldAnimateScroll: this.shouldAnimateScroll });
						} else {
							this.tocBookDesktopModule.refreshStateOnResize();
						}
					}
				} else if (!this.media && this.$tocMobile) {
					if (!this.mobileModule) {
						this.mobileModule = new this.TocMobileClass(this.$tocMobile, { shouldAnimateScroll: this.shouldAnimateScroll });
					} else if (this.mobileModule.refreshStateOnResize) {
						this.mobileModule.refreshStateOnResize();
					}
				}
			}, 20));
		}
	}
}